import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLocalization } from '../hooks/useLocalization';
import { useNavigate } from 'react-router-dom';

export const ProfilePage: React.FC = () => {
    const { user, isAuthenticated, isLoading, updateProfile, logout } = useAuth();
    const { t, language } = useLocalization();
    const navigate = useNavigate();

    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [success, setSuccess] = useState('');
    const [form, setForm] = useState({
        firstName: '',
        lastName: '',
        phone: '',
        address: '',
        city: '',
        zipCode: '',
        country: '',
        nationality: '',
        gender: '' as 'male' | 'female' | '',
    });

    useEffect(() => {
        if (!isLoading && !isAuthenticated) {
            navigate('/login');
        }
    }, [isLoading, isAuthenticated, navigate]);

    useEffect(() => {
        if (user?.profile) {
            setForm({
                firstName: user.profile.firstName || '',
                lastName: user.profile.lastName || '',
                phone: user.profile.phone || '',
                address: user.profile.address || '',
                city: user.profile.city || '',
                zipCode: user.profile.zipCode || '',
                country: user.profile.country || '',
                nationality: user.profile.nationality || '',
                gender: user.profile.gender || '',
            });
        }
    }, [user]);

    const handleSave = async () => {
        setSaving(true);
        setSuccess('');
        const ok = await updateProfile(form);
        setSaving(false);
        if (ok) {
            setSuccess(language === 'sv' ? 'Profilen har sparats!' : 'Profile saved!');
            setEditing(false);
            setTimeout(() => setSuccess(''), 3000);
        }
    };

    const handleLogout = async () => {
        await logout();
        navigate('/');
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="w-8 h-8 border-3 border-gray-300 border-t-teal-600 rounded-full animate-spin"></div>
            </div>
        );
    }

    if (!user) return null;

    const sv = language === 'sv';
    const initials = ((user.profile?.firstName?.[0] || '') + (user.profile?.lastName?.[0] || '')).toUpperCase() || user.email[0].toUpperCase();

    return (
        <div className="bg-gray-50 min-h-screen py-10">
            <div className="container mx-auto px-4 max-w-3xl">
                {/* Header */}
                <div className="flex items-center justify-between mb-8">
                    <h1 className="text-3xl font-bold text-gray-900">{sv ? 'Min Profil' : 'My Profile'}</h1>
                    <button
                        onClick={() => navigate('/bookings')}
                        className="text-sm font-medium text-teal-600 hover:text-teal-800 transition-colors"
                    >
                        {sv ? 'Mina Bokningar →' : 'My Bookings →'}
                    </button>
                </div>

                {/* Success message */}
                {success && (
                    <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl text-green-700 text-sm font-medium flex items-center gap-2">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                        {success}
                    </div>
                )}

                {/* Profile Card */}
                <div className="bg-white rounded-2xl shadow-lg border border-gray-200/80 overflow-hidden">
                    {/* Avatar + Email Header */}
                    <div className="bg-gradient-to-r from-teal-600 to-teal-500 px-6 py-8 text-white">
                        <div className="flex items-center gap-4">
                            {user.avatarUrl ? (
                                <img src={user.avatarUrl} alt="" className="w-16 h-16 rounded-full border-2 border-white/30 object-cover" />
                            ) : (
                                <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center text-2xl font-bold">
                                    {initials}
                                </div>
                            )}
                            <div>
                                <p className="text-xl font-bold">
                                    {user.profile?.firstName && user.profile?.lastName
                                        ? `${user.profile.firstName} ${user.profile.lastName}`
                                        : user.email}
                                </p>
                                <p className="text-teal-100 text-sm">{user.email}</p>
                                <div className="flex items-center gap-2 mt-1">
                                    <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">
                                        {user.provider === 'google' ? 'Google' : 'Email'}
                                    </span>
                                    <span className="text-xs text-teal-200">
                                        {sv ? 'Medlem sedan' : 'Member since'} {new Date(user.createdAt).toLocaleDateString(sv ? 'sv-SE' : 'en-US', { year: 'numeric', month: 'short' })}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Form */}
                    <div className="px-6 py-6">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-lg font-bold text-gray-900">{sv ? 'Personuppgifter' : 'Personal Information'}</h2>
                            {!editing && (
                                <button
                                    onClick={() => setEditing(true)}
                                    className="text-sm font-medium text-teal-600 hover:text-teal-800 transition-colors flex items-center gap-1"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                                    {sv ? 'Redigera' : 'Edit'}
                                </button>
                            )}
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {/* First Name */}
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">{sv ? 'Förnamn' : 'First Name'}</label>
                                {editing ? (
                                    <input type="text" value={form.firstName} onChange={e => setForm(f => ({ ...f, firstName: e.target.value }))}
                                        className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none transition-shadow" />
                                ) : (
                                    <p className="text-gray-900 text-sm py-2.5">{form.firstName || <span className="text-gray-400 italic">{sv ? 'Ej ifyllt' : 'Not set'}</span>}</p>
                                )}
                            </div>
                            {/* Last Name */}
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">{sv ? 'Efternamn' : 'Last Name'}</label>
                                {editing ? (
                                    <input type="text" value={form.lastName} onChange={e => setForm(f => ({ ...f, lastName: e.target.value }))}
                                        className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none transition-shadow" />
                                ) : (
                                    <p className="text-gray-900 text-sm py-2.5">{form.lastName || <span className="text-gray-400 italic">{sv ? 'Ej ifyllt' : 'Not set'}</span>}</p>
                                )}
                            </div>
                            {/* Phone */}
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">{sv ? 'Telefon' : 'Phone'}</label>
                                {editing ? (
                                    <input type="tel" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                                        className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none transition-shadow" />
                                ) : (
                                    <p className="text-gray-900 text-sm py-2.5">{form.phone || <span className="text-gray-400 italic">{sv ? 'Ej ifyllt' : 'Not set'}</span>}</p>
                                )}
                            </div>
                            {/* Gender */}
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">{sv ? 'Kön' : 'Gender'}</label>
                                {editing ? (
                                    <select value={form.gender} onChange={e => setForm(f => ({ ...f, gender: e.target.value as 'male' | 'female' | '' }))}
                                        className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none transition-shadow bg-white">
                                        <option value="">{sv ? 'Välj...' : 'Select...'}</option>
                                        <option value="male">{sv ? 'Man' : 'Male'}</option>
                                        <option value="female">{sv ? 'Kvinna' : 'Female'}</option>
                                    </select>
                                ) : (
                                    <p className="text-gray-900 text-sm py-2.5">{form.gender ? (form.gender === 'male' ? (sv ? 'Man' : 'Male') : (sv ? 'Kvinna' : 'Female')) : <span className="text-gray-400 italic">{sv ? 'Ej ifyllt' : 'Not set'}</span>}</p>
                                )}
                            </div>
                            {/* Address */}
                            <div className="sm:col-span-2">
                                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">{sv ? 'Adress' : 'Address'}</label>
                                {editing ? (
                                    <input type="text" value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))}
                                        className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none transition-shadow" />
                                ) : (
                                    <p className="text-gray-900 text-sm py-2.5">{form.address || <span className="text-gray-400 italic">{sv ? 'Ej ifyllt' : 'Not set'}</span>}</p>
                                )}
                            </div>
                            {/* City */}
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">{sv ? 'Stad' : 'City'}</label>
                                {editing ? (
                                    <input type="text" value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))}
                                        className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none transition-shadow" />
                                ) : (
                                    <p className="text-gray-900 text-sm py-2.5">{form.city || <span className="text-gray-400 italic">{sv ? 'Ej ifyllt' : 'Not set'}</span>}</p>
                                )}
                            </div>
                            {/* Zip Code */}
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">{sv ? 'Postnummer' : 'Zip Code'}</label>
                                {editing ? (
                                    <input type="text" value={form.zipCode} onChange={e => setForm(f => ({ ...f, zipCode: e.target.value }))}
                                        className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none transition-shadow" />
                                ) : (
                                    <p className="text-gray-900 text-sm py-2.5">{form.zipCode || <span className="text-gray-400 italic">{sv ? 'Ej ifyllt' : 'Not set'}</span>}</p>
                                )}
                            </div>
                            {/* Country */}
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">{sv ? 'Land' : 'Country'}</label>
                                {editing ? (
                                    <input type="text" value={form.country} onChange={e => setForm(f => ({ ...f, country: e.target.value }))}
                                        className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none transition-shadow" />
                                ) : (
                                    <p className="text-gray-900 text-sm py-2.5">{form.country || <span className="text-gray-400 italic">{sv ? 'Ej ifyllt' : 'Not set'}</span>}</p>
                                )}
                            </div>
                            {/* Nationality */}
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">{sv ? 'Nationalitet' : 'Nationality'}</label>
                                {editing ? (
                                    <input type="text" value={form.nationality} onChange={e => setForm(f => ({ ...f, nationality: e.target.value }))}
                                        className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none transition-shadow" />
                                ) : (
                                    <p className="text-gray-900 text-sm py-2.5">{form.nationality || <span className="text-gray-400 italic">{sv ? 'Ej ifyllt' : 'Not set'}</span>}</p>
                                )}
                            </div>
                        </div>

                        {/* Edit actions */}
                        {editing && (
                            <div className="flex items-center gap-3 mt-6 pt-4 border-t border-gray-100">
                                <button
                                    onClick={handleSave}
                                    disabled={saving}
                                    className="px-6 py-2.5 bg-teal-600 text-white font-semibold rounded-xl hover:bg-teal-700 transition-colors disabled:opacity-50 text-sm"
                                >
                                    {saving ? (sv ? 'Sparar...' : 'Saving...') : (sv ? 'Spara' : 'Save')}
                                </button>
                                <button
                                    onClick={() => { setEditing(false); if (user?.profile) setForm({ firstName: user.profile.firstName || '', lastName: user.profile.lastName || '', phone: user.profile.phone || '', address: user.profile.address || '', city: user.profile.city || '', zipCode: user.profile.zipCode || '', country: user.profile.country || '', nationality: user.profile.nationality || '', gender: user.profile.gender || '' }); }}
                                    className="px-6 py-2.5 text-gray-600 font-semibold rounded-xl hover:bg-gray-100 transition-colors text-sm"
                                >
                                    {sv ? 'Avbryt' : 'Cancel'}
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* Logout */}
                <div className="mt-6 text-center">
                    <button
                        onClick={handleLogout}
                        className="text-sm text-red-500 hover:text-red-700 font-medium transition-colors"
                    >
                        {sv ? 'Logga ut' : 'Log out'}
                    </button>
                </div>
            </div>
        </div>
    );
};
